/**
 * Project page: upload IFC models (Wave 1 parse pipeline wired to the API)
 * and jump to the readiness report / normalization wizard per model.
 *
 * Models are session-scoped (the Wave 2 contract has no list-models
 * endpoint — TODO-Wave3: list persisted models here).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, ChangeEvent, DragEvent } from "react";
import { useParams } from "@tanstack/react-router";
import { uploadIfcModel, type UploadProgress } from "../api/modelUpload";
import { useIstariStore } from "../state/store";

const PHASE_LABEL: Record<UploadProgress["phase"], string> = {
  opening: "Opening IFC…",
  extracting: "Extracting storeys & spaces…",
  converting: "Converting to Fragments…",
  uploading: "Uploading…",
  saving: "Saving summary & fragments…",
};

const cardStyle: CSSProperties = {
  background: "#21213a",
  borderRadius: 8,
  padding: "0.8rem 0.9rem",
  marginBottom: "0.6rem",
};

export function ProjectPage() {
  const params = useParams({ strict: false }) as { projectId?: string };
  const projectId = params.projectId ?? "";

  const projects = useIstariStore((s) => s.projects);
  const projectsStatus = useIstariStore((s) => s.projectsStatus);
  const loadProjects = useIstariStore((s) => s.loadProjects);
  const models = useIstariStore((s) => s.models);
  const registerModel = useIstariStore((s) => s.registerModel);
  const loadReadiness = useIstariStore((s) => s.loadReadiness);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (projectsStatus === "idle") void loadProjects();
  }, [projectsStatus, loadProjects]);

  const project = projects.find((p) => p.id === projectId) ?? null;
  const projectModels = models.filter((m) => m.projectId === projectId);

  const uploadFile = useCallback(
    async (file: File) => {
      setError(null);
      setProgress({ phase: "uploading", ratio: 0 });
      try {
        const uploaded = await uploadIfcModel(projectId, file, setProgress);
        registerModel({
          modelId: uploaded.modelId,
          projectId,
          fileName: uploaded.fileName,
          summary: uploaded.summary,
          // `fragments === null` means the worker's 3D (Fragments)
          // conversion failed for this file — the summary/2D data (wizard,
          // readiness, plan view) is still fully usable, but the 3D viewer
          // has nothing to render. Surfaced as a visible badge below rather
          // than looking identical to a fully successful upload.
          threeDAvailable: uploaded.fragments !== null,
        });
        void loadReadiness(uploaded.modelId);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setProgress(null);
      }
    },
    [projectId, registerModel, loadReadiness],
  );

  const onFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (file) void uploadFile(file);
    },
    [uploadFile],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setDragging(false);
      if (progress) return;
      const file = Array.from(event.dataTransfer.files).find((f) =>
        f.name.toLowerCase().endsWith(".ifc"),
      );
      if (file) {
        void uploadFile(file);
      } else if (event.dataTransfer.files.length > 0) {
        setError("Only .ifc files are supported.");
      }
    },
    [uploadFile, progress],
  );

  return (
    <main
      data-testid="project-page"
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      style={{
        fontFamily: "sans-serif",
        padding: "2rem",
        maxWidth: 720,
        margin: "0 auto",
        background: "#1a1a2e",
        color: "#d8d8e8",
        minHeight: "100vh",
        boxSizing: "border-box",
        outline: dragging ? "3px dashed #4a90d9" : "none",
        outlineOffset: -3,
      }}
    >
      <nav style={{ marginBottom: "1rem" }}>
        <a href="/" style={{ color: "#8e8eb8" }}>
          ← Projects
        </a>
      </nav>
      <h1 data-testid="project-title" style={{ marginTop: 0 }}>
        {project?.name ?? "Project"}
      </h1>
      {project === null && projectsStatus === "ready" && (
        <p data-testid="project-missing" style={{ color: "#e06c75" }}>
          Project not found.
        </p>
      )}

      <section style={cardStyle}>
        <h2 style={{ margin: "0 0 0.5rem", fontSize: "1rem" }}>Upload IFC model</h2>
        <input
          ref={fileInputRef}
          data-testid="model-file-input"
          type="file"
          accept=".ifc"
          onChange={onFileChange}
          disabled={progress !== null}
          style={{ display: "none" }}
        />
        <button
          type="button"
          data-testid="model-upload-button"
          onClick={() => fileInputRef.current?.click()}
          disabled={progress !== null}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: 6,
            border: "1px solid #4a90d9",
            background: progress ? "#2a2a44" : "#4a90d9",
            color: "#fff",
            cursor: progress ? "wait" : "pointer",
          }}
        >
          {progress ? "Working…" : "Upload IFC file"}
        </button>
        <span style={{ marginLeft: "0.6rem", color: "#8e8eb8" }}>
          …or drag &amp; drop an .ifc file.
        </span>
        {progress && (
          <div data-testid="upload-progress" style={{ marginTop: "0.6rem" }}>
            <div style={{ marginBottom: "0.3rem" }}>{PHASE_LABEL[progress.phase]}</div>
            <div
              style={{
                height: 6,
                borderRadius: 3,
                background: "#2a2a44",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.round(progress.ratio * 100)}%`,
                  background: "#4a90d9",
                  transition: "width 120ms linear",
                }}
              />
            </div>
          </div>
        )}
        {error && (
          <p data-testid="upload-error" style={{ color: "#e06c75" }}>
            {error}
          </p>
        )}
      </section>

      <h2 style={{ fontSize: "1rem" }}>Models (this session)</h2>
      {projectModels.length === 0 && (
        <p data-testid="models-empty" style={{ color: "#8e8eb8" }}>
          No models uploaded in this session yet.
        </p>
      )}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {projectModels.map((model) => (
          <li key={model.modelId} data-testid="model-row" style={cardStyle}>
            <div style={{ fontWeight: 600 }}>
              {model.fileName}
              {model.threeDAvailable === false && (
                <span
                  data-testid="model-3d-unavailable-badge"
                  title="3D (Fragments) conversion failed for this upload — 2D data (readiness, wizard, plan view) is still available, but there is no 3D view."
                  style={{
                    marginLeft: "0.5rem",
                    padding: "0.1rem 0.4rem",
                    borderRadius: 4,
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    background: "#4a3a1a",
                    color: "#e0b060",
                  }}
                >
                  2D only
                </span>
              )}
            </div>
            <div style={{ color: "#8e8eb8", fontSize: "0.82rem" }}>
              {model.summary.schema} · {model.summary.storeys.length} storeys ·{" "}
              {model.summary.spaces.length} spaces
            </div>
            <div style={{ marginTop: "0.4rem", display: "flex", gap: "1rem" }}>
              <a href={`/models/${model.modelId}/readiness`} style={{ color: "#4a90d9" }}>
                Readiness report
              </a>
              <a href={`/models/${model.modelId}/normalize`} style={{ color: "#4a90d9" }}>
                Normalization wizard
              </a>
              <a href={`/models/${model.modelId}/mep`} style={{ color: "#4a90d9" }}>
                MEP generation
              </a>
            </div>
          </li>
        ))}
      </ul>
    </main>
  );
}
